import type { ReactNode } from 'react'
import type { DimensionValue } from 'react-native'
import { Text, View } from 'react-native'
import Animated, {
  useAnimatedStyle,
  useDerivedValue,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated'
import Svg, { Path } from 'react-native-svg'
import { captureException } from '@/src/utils/sentry'
import { useTranslation } from 'react-i18next'

import { QuerySuspenseBoundary } from '../../../components/ui/QuerySuspenseBoundary'
import { SectionErrorState } from '../../../components/ui/SectionErrorState'
import { colors } from '../../../theme'
import { body4, label1 } from '../../../theme/typography'
import { useWeeklyStreakWidgetContent } from '../hooks/useWeeklyStreakWidgetContent'

const VALUE_LINE_HEIGHT = 24

const valueTextStyle = {
  ...label1,
  color: colors.textHeader,
  lineHeight: VALUE_LINE_HEIGHT,
}

type WeeklyMetricItemProps = {
  icon: ReactNode
  label: string
  value: string
  valueTestID: string
}

export type WeeklyStreakWidgetContent = {
  exercisesLabel: string
  exercisesValue: string
  exerciseGoalLabel: string
  exerciseGoalValue: string
  streakLabel: string
  streakValue: string
}

function useSkeletonAnimatedStyle() {
  const opacity = useDerivedValue(() =>
    withRepeat(
      withSequence(
        withTiming(0.7, { duration: 1000 }),
        withTiming(0.3, { duration: 1000 }),
      ),
      -1,
      true,
    ),
  )

  return useAnimatedStyle(() => ({
    opacity: opacity.value,
  }))
}

const ValueSkeleton = ({
  testID,
  width = '74%',
}: {
  testID: string
  width?: DimensionValue
}) => {
  const animatedStyle = useSkeletonAnimatedStyle()

  return (
    <Animated.View
      testID={testID}
      style={[
        {
          width,
          minWidth: 56,
          height: VALUE_LINE_HEIGHT,
          borderRadius: 4,
          backgroundColor: colors.buttonSecondary,
        },
        animatedStyle,
      ]}
    />
  )
}

const WeeklyMetricItemSkeleton = ({
  testID,
  valueWidth,
}: {
  testID: string
  valueWidth: DimensionValue
}) => {
  const animatedStyle = useSkeletonAnimatedStyle()

  return (
    <View
      testID={testID}
      className="flex-col justify-center"
      style={{
        minHeight: 54,
        width: '31.5%',
      }}
    >
      <View className="mb-1 flex-row items-center">
        <Animated.View
          className="rounded-full"
          style={[
            {
              width: 20,
              height: 20,
              backgroundColor: colors.buttonSecondary,
            },
            animatedStyle,
          ]}
        />
        <Animated.View
          className="ml-1 rounded"
          style={[
            {
              width: '58%',
              height: 12,
              backgroundColor: colors.buttonSecondary,
            },
            animatedStyle,
          ]}
        />
      </View>
      <ValueSkeleton testID={`${testID}-value`} width={valueWidth} />
    </View>
  )
}

function WeeklyStreakWidgetSkeleton() {
  return (
    <View
      testID="weekly-streak-widget-skeleton"
      className="bg-background-secondary rounded-lg border border-gray-50 flex-row items-stretch justify-between px-4 py-3"
      style={{
        marginBottom: 8,
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
      }}
    >
      <WeeklyMetricItemSkeleton
        testID="weekly-streak-widget-exercises-skeleton"
        valueWidth="74%"
      />
      <WeeklyMetricItemSkeleton
        testID="weekly-streak-widget-goal-skeleton"
        valueWidth="64%"
      />
      <WeeklyMetricItemSkeleton
        testID="weekly-streak-widget-streak-skeleton"
        valueWidth="52%"
      />
    </View>
  )
}

const WeeklyMetricItem = ({
  icon,
  label,
  value,
  valueTestID,
}: WeeklyMetricItemProps) => {
  return (
    <View
      className="flex-col justify-center"
      style={{
        minHeight: 54,
        width: '31.5%',
      }}
    >
      <View className="mb-1 flex-row items-center">
        <View style={{ marginLeft: -2 }}>{icon}</View>
        <Text
          className=""
          style={{
            ...body4,
            marginLeft: 4,
            color: colors.textBody,
          }}
          numberOfLines={1}
        >
          {label}
        </Text>
      </View>
      <View
        style={{
          height: VALUE_LINE_HEIGHT,
          justifyContent: 'center',
        }}
      >
        <Text testID={valueTestID} className="" style={valueTextStyle}>
          {value}
        </Text>
      </View>
    </View>
  )
}

const ExercisesIcon = () => (
  <Svg width={20} height={20} viewBox="0 0 20 20" fill="none">
    <Path
      d="M8.94 10.88L7.69 9.65C7.53 9.49 7.36 9.42 7.17 9.42C6.97 9.42 6.8 9.49 6.65 9.65C6.49 9.8 6.42 9.98 6.42 10.18C6.42 10.38 6.49 10.56 6.65 10.71L8.42 12.48C8.57 12.63 8.74 12.71 8.94 12.71C9.13 12.71 9.31 12.63 9.46 12.48L13.35 8.58C13.51 8.43 13.58 8.25 13.58 8.05C13.58 7.85 13.51 7.67 13.35 7.52C13.2 7.37 13.03 7.29 12.83 7.29C12.64 7.29 12.47 7.37 12.31 7.52L8.94 10.88ZM10 18C8.9 18 7.87 17.79 6.9 17.38C5.92 16.96 5.07 16.39 4.34 15.66C3.61 14.93 3.04 14.08 2.63 13.1C2.21 12.13 2 11.1 2 10C2 8.89 2.21 7.85 2.63 6.89C3.04 5.92 3.61 5.07 4.34 4.34C5.07 3.61 5.92 3.04 6.9 2.63C7.87 2.21 8.9 2 10 2C11.11 2 12.15 2.21 13.11 2.63C14.08 3.04 14.93 3.61 15.66 4.34C16.39 5.07 16.96 5.92 17.38 6.89C17.79 7.85 18 8.89 18 10C18 11.1 17.79 12.13 17.38 13.1C16.96 14.08 16.39 14.93 15.66 15.66C14.93 16.39 14.08 16.96 15.11 17.38C12.15 17.79 11.11 18 10 18Z"
      fill="#291180"
    />
  </Svg>
)

const ExerciseGoalIcon = () => (
  <Svg width={20} height={20} viewBox="0 0 20 20" fill="none">
    <Path
      d="M5.5 11V17.25C5.5 17.46 5.43 17.64 5.29 17.78C5.14 17.93 4.97 18 4.75 18C4.54 18 4.36 17.93 4.22 17.78C4.07 17.64 4 17.46 4 17.25V3.75C4 3.54 4.07 3.36 4.22 3.22C4.36 3.07 4.54 3 4.75 3H15.15C15.29 3 15.43 3.04 15.54 3.11C15.65 3.18 15.75 3.28 15.81 3.4C15.88 3.51 15.91 3.63 15.91 3.76C15.9 3.89 15.86 4.02 15.79 4.15L14 7L15.79 9.85C15.86 9.98 15.9 10.11 15.91 10.24C15.91 10.37 15.88 10.49 15.81 10.6C15.74 10.72 15.65 10.81 15.54 10.89C15.42 10.96 15.29 11 15.15 11H5.5Z"
      fill="#E30B5D"
    />
  </Svg>
)

const StreakIcon = () => (
  <Svg width={20} height={20} viewBox="0 0 20 20" fill="none">
    <Path
      d="M16 16.75C16.21 16.75 16.39 16.68 16.53 16.54C16.68 16.39 16.75 16.22 16.75 16C16.75 15.79 16.68 15.61 16.54 15.47C16.39 15.32 16.22 15.25 16 15.25C15.79 15.25 15.61 15.32 15.47 15.46C15.32 15.61 15.25 15.78 15.25 16C15.25 16.21 15.32 16.39 15.46 16.53C15.61 16.68 15.78 16.75 16 16.75ZM10 18C8.68 18 7.44 17.7 6.27 17.1C5.1 16.51 4.17 15.69 3.48 14.65L7 11.1L8.98 13.01C9.13 13.17 9.31 13.25 9.5 13.25C9.69 13.25 9.87 13.17 10.02 13.02L13.5 9.54V10.25C13.5 10.46 13.57 10.64 13.71 10.78C13.86 10.93 14.03 11 14.25 11C14.46 11 14.64 10.93 14.78 10.78C14.93 10.64 15 10.46 15 10.25V7.75C15 7.54 14.93 7.36 14.78 7.22C14.64 7.07 14.46 7 14.25 7H11.75C11.54 7 11.36 7.07 11.22 7.21C11.07 7.36 11 7.53 11 7.75C11 7.96 11.07 8.14 11.22 8.28C11.36 8.43 11.54 8.5 11.75 8.5H12.44L9.5 11.44L7.52 9.51C7.37 9.37 7.19 9.29 7 9.29C6.81 9.29 6.63 9.37 6.48 9.52L2.71 13.29C2.47 12.76 2.3 12.23 2.18 11.68C2.06 11.13 2 10.57 2 10C2 8.89 2.21 7.85 2.63 6.89C3.04 5.92 3.61 5.07 4.34 4.34C5.07 3.61 5.92 3.04 6.9 2.63C7.87 2.21 8.9 2 10.01 2C11.11 2 12.15 2.21 13.11 2.63C14.08 3.04 14.93 3.61 15.66 4.34C16.39 5.07 16.96 5.92 17.38 6.89C17.79 7.85 18 8.89 18 10V16.5C18 16.91 17.85 17.27 17.56 17.56C17.27 17.85 16.91 18 16.5 18H10Z"
      fill="#FFB104"
    />
  </Svg>
)

function WeeklyStreakWidgetErrorFallback({
  reset,
}: Readonly<{ reset: () => void }>) {
  const { t } = useTranslation(['dashboard', 'common'])

  return (
    <SectionErrorState
      testID="weekly-streak-widget-error"
      title={t('dashboard:weekly_widget_error_title')}
      message={t('dashboard:weekly_widget_error_message')}
      onRetry={reset}
    />
  )
}

function ReadyWeeklyStreakWidget() {
  const content = useWeeklyStreakWidgetContent()

  return (
    <View
      className="bg-background-secondary rounded-lg border border-gray-50 flex-row items-stretch justify-between px-4 py-3"
      style={{
        marginBottom: 8,
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
      }}
    >
      <WeeklyMetricItem
        icon={<ExercisesIcon />}
        label={content.exercisesLabel}
        value={content.exercisesValue}
        valueTestID="weekly-streak-widget-exercises-value"
      />
      <WeeklyMetricItem
        icon={<ExerciseGoalIcon />}
        label={content.exerciseGoalLabel}
        value={content.exerciseGoalValue}
        valueTestID="weekly-streak-widget-exercise-goal-value"
      />
      <WeeklyMetricItem
        icon={<StreakIcon />}
        label={content.streakLabel}
        value={content.streakValue}
        valueTestID="weekly-streak-widget-streak-value"
      />
    </View>
  )
}

export const WeeklyStreakWidget = () => {
  return (
    <QuerySuspenseBoundary
      loadingFallback={<WeeklyStreakWidgetSkeleton />}
      errorFallback={({ reset }) => (
        <WeeklyStreakWidgetErrorFallback reset={reset} />
      )}
      onError={(error) => {
        captureException(error, {
          feature: 'dashboard',
          tags: { component: 'WeeklyStreakWidget' },
        })
      }}
    >
      <ReadyWeeklyStreakWidget />
    </QuerySuspenseBoundary>
  )
}
